import { sortWithOrder } from '@konova/ui/sort-with-order';
import uniqBy from 'lodash/uniqBy';
import { create } from 'zustand';
import { ISurveyDocument } from '../../../types';

export type SurveyDocumentStoreUpdatePayload = Partial<ISurveyDocument> & Required<Pick<ISurveyDocument, 'id'>>;

export type SurveyDocumentStoreState = {
  /**
   * A variable used to store a collection of survey documents, where each document is associated with a unique number key.
   */
  documents: ISurveyDocument[];

  /**
   * Updates a survey document.
   */
  update: (document: SurveyDocumentStoreUpdatePayload) => void;

  /**
   * Removes a document from the survey.
   */
  remove: (document: Pick<ISurveyDocument, 'id'>) => void;

  /**
   * Adds multiple survey documents to the collection.
   */
  addAll: (documents: ISurveyDocument[]) => void;

  /**
   * Updates the current set of survey documents with the provided array of documents.
   */
  set: (documents: ISurveyDocument[]) => void;

  /**
   * Returns an array of survey documents based on their IDs.
   */
  getAllById: (documents: Pick<ISurveyDocument, 'id'>[]) => ISurveyDocument[];

  /**
   *
   */
  getById: (id: number) => ISurveyDocument | null;

  /**
   *
   */
  sort: (documents: Pick<ISurveyDocument, 'id'>[]) => void;
};

// @Todo rename this store to avoid conflicts. Should be `SurveyDocumentsRepositoryStore` or something like that
export const useSurveyDocumentStore = create<SurveyDocumentStoreState>((set, get) => ({
  documents: [],
  addAll: documents =>
    set(state => ({
      ...state,
      documents: uniqBy([...state.documents, ...documents], 'id'),
    })),

  set: documents =>
    set(state => ({
      ...state,
      documents,
    })),

  update: document =>
    set(state => ({
      ...state,
      documents: state.documents.map(doc => {
        if (document.id === doc.id) {
          return { ...doc, ...document };
        }
        return doc;
      }),
    })),

  remove: document =>
    set(state => ({
      ...state,
      documents: state.documents.filter(doc => {
        return doc.id !== document.id;
      }),
    })),

  getById: id => {
    return get().documents.find(doc => doc.id === id) ?? null;
  },

  sort: (list: Pick<ISurveyDocument, 'id'>[]) => {
    const order = list.map(doc => doc.id!); // Extract the order of IDs from the input list

    set(state => ({
      ...state,
      documents: sortWithOrder<ISurveyDocument & { id: number }>(state.documents as (ISurveyDocument & { id: number })[], order),
    }));
  },

  getAllById: documents => {
    const ids = documents.map(doc => doc.id!);
    return get().documents.filter(({ id }) => ids.includes(id!));
  },
}));
